// JS implementation of UPGMA clustering with no dependencies
// https://en.wikipedia.org/wiki/UPGMA

// this is 'first-draft' quality code - there are plenty of optimizations to be
// made here if performance is an issue

const sum = array => array.reduce((a, b) => a + b)

const scalarAvgFn = array => sum(array) / array.length
const scalarDistanceFn = (a, b) => Math.abs(a - b)

const minIndex = array => array.reduce((iMin, x, i) => x < array[iMin] ? i : iMin, 0)
const range = length => Array.apply(null, Array(length)).map((_, i) => i)

const defaultOpts = {
  getDistanceFn: scalarDistanceFn,
  getAvgFn: scalarAvgFn
}

// cluster an array of numeric values such that the mean difference of each
// point within each cluster is within a threshold value
export const cluster = (points, threshold = 10, _opts = {}) => {
  const opts = {...defaultOpts, ..._opts}
  const clusters = _cluster(
    points,
    range(points.length).map(i => [i]),
    threshold,
    opts
  )
  return clusters
}

// recursive call
export const _cluster = (points, clusters, threshold, opts) => {
  if (clusters.length < 2) {
    return clusters
  }
  const matrix = getDistanceMatrix(points, clusters, opts)
  // get the minimum col index for each row in the matrix
  const rowMinimums = matrix.map(minIndex)
  // get the index for the column containing the smallest distance
  const bestRow = minIndex(rowMinimums.map((col, row) => matrix[row][col]))
  const bestCol = rowMinimums[bestRow]
  const isValid = isValidCluster(points, mergeClusters(clusters[bestRow], clusters[bestCol]), threshold, opts)

  if (!isValid) {
    return clusters
  }

  return _cluster(points, merge(clusters, bestRow, bestCol), threshold, opts)
}

const isValidCluster = (points, cluster, threshold, opts) => {
  // at this point, cluster is guaranteed to be an array, not a single point
  const clusterPoints = cluster.map(i => points[i])
  const mean = opts.getAvgFn(clusterPoints)
  return clusterPoints.every(d => opts.getDistanceFn(mean, d) <= threshold)
}

// immutable merge of indices a and b in clusters
const merge = (clusters, a, b) => {
  // merge two clusters by index
  const clusterA = clusters[a]
  const clusterB = clusters[b]
  // optimization opportunity: this filter is causing *another* iteration
  // of clusters.
  const withoutPoints = clusters.filter(c => c !== clusterA && c !== clusterB)

  return [mergeClusters(clusterA, clusterB)].concat(withoutPoints)
}

const mergeClusters = (clusterA, clusterB) => clusterA.concat(clusterB)
// optimization opportunity: this currently does 2x the work needed, since the
// distance from a->b is the same as the distance from b->a

const getDistanceMatrix = (points, clusters, opts) => {
  // reduce clusters to distance/average distance
  const reduced = clusters.map(c => Array.isArray(c) ? opts.getAvgFn(c.map(i => points[i])) : points[c])
  return reduced.map((i, row) => reduced.map((j, col) => (row === col) ? Infinity : opts.getDistanceFn(j, i)))
}

// const distances = [5, 8, 8, 9, 16, 20, 29, 42, 56, 57, 57, 58, 103, 104, 150, 167, 800]
// const log2DArray = rows => console.log('[\n' + rows.map(row => '  [' + row.join(', ') + ']').join('\n') + '\n]')
// console.log('clustered points:')
// log2DArray(cluster(distances))
